import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { readPanelTheme, writePanelTheme, type PanelTheme } from '../customiser/panel-theme';

/**
 * The console's light/dark preference — platform-wide, not per screen.
 *
 * Two attributes go on `<html>`: `data-theme` ("light" | "dark" |
 * "system"), which the headless tokens and Tailwind's `dark:` variant
 * read (src/theme/headless/), and the older `data-cz-theme` beside it
 * (absent under 'system') with the `cz-root` class, which the
 * customiser's `cz-*` tokens hang off.
 *
 * They go on `<html>`, not on a React root, for one reason that isn't
 * obvious: the `dark:` variant is keyed on the root's `data-theme`,
 * and Headless UI renders every
 * dropdown, dialog and popover into a portal at `document.body` —
 * OUTSIDE any React-rendered wrapper. Stamped on a nested div, the
 * variant simply never matches inside a portal, so menus rendered light
 * on a dark page. On `<html>` everything is a descendant, portals
 * included.
 *
 * The stored value is still `cz-theme` in localStorage and still means
 * exactly what it did when this was the customiser panel's own switch,
 * so an existing preference carries over rather than resetting. With
 * nothing stored the console is dark (customiser/panel-theme.ts).
 * index.html stamps the same `data-theme` before the bundle runs, so a
 * light choice never paints the dark ground first (theme.test.tsx holds
 * the two to the same answer).
 */
export type Theme = PanelTheme;

/** What the screen is actually showing, once 'system' has been asked. */
export type Scheme = 'light' | 'dark';

interface ThemeValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const ThemeCtx = createContext<ThemeValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readPanelTheme());

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('cz-root');
    root.dataset.theme = theme;
    // The older attribute keeps its old meaning: 'system' is its absence.
    if (theme === 'system') delete root.dataset.czTheme;
    else root.dataset.czTheme = theme;
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    writePanelTheme(t);
    setThemeState(t);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

function subscribeToOs(onChange: () => void) {
  // jsdom has no matchMedia; a test rendering under 'system' is light.
  if (typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(DARK_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function osIsDark() {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/**
 * The scheme in effect, for the one consumer that cannot read it from
 * CSS. Everything the console draws itself gets light and dark from the
 * `dark:` variant and never needs to know which one it got; Clerk's
 * components are handed colours as VALUES (lib/clerk-appearance.ts), so
 * something has to resolve the preference into one of two answers and
 * re-resolve it when the OS changes under a 'system' preference.
 *
 * Reads the same media query the headless tokens do under
 * `data-theme="system"`, so this and the `dark:` variant can never
 * disagree about which mode the page is in.
 */
export function useScheme(): Scheme {
  // Outside a ThemeProvider (a screen rendered on its own, as the
  // customiser's tests do) the preference is 'system': the OS decides.
  const theme = useContext(ThemeCtx)?.theme ?? 'system';
  const dark = useSyncExternalStore(subscribeToOs, osIsDark, () => false);
  if (theme === 'system') return dark ? 'dark' : 'light';
  return theme;
}
